import { isEmpty, keyBy } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AIOnboardingModalProviderProps } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { groupSuggestionsByFormPath } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.utils';
import {
  AIOnboardingModalContext,
  AIOnboardingModalContextShape,
} from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { useOnboardingPrincipalNodes } from '@/modules/aiOnboarding/AIOnboardingModal/context/useOnboardingPrincipalNodes';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';

function useContextValue(
  props: AIOnboardingModalProviderProps
): AIOnboardingModalContextShape {
  const {
    isOpen,
    onClose,
    loading,
    variant,
    suggestions,
    suggestionsForSubmission,
    suggestionsPagination,
    additionalReferenceSuggestions,
    household,
    footerRef,
    skipNavigationOnComplete,
    reviewedSuggestionIDs,
    onComplete,
    onSuggestionSaved,
  } = props;
  const operationInFlightRef = useRef(false);
  const [isOperationInFlight, setIsOperationInFlight] = useState(false);

  const beginOperation = useCallback(() => {
    if (operationInFlightRef.current) {
      return false;
    }

    operationInFlightRef.current = true;
    setIsOperationInFlight(true);
    return true;
  }, []);

  const endOperation = useCallback(() => {
    operationInFlightRef.current = false;
    setIsOperationInFlight(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!operationInFlightRef.current) {
      onClose();
    }
  }, [onClose]);

  // Reviews made before this page mounted (this provider remounts per page).
  // Deliberately a mount-time snapshot, not reactive: reviewing a row on the
  // current page removes its form row, while rows reviewed on a previous visit
  // must not be rebuilt into the form.
  const [previouslyReviewedSuggestionIDs] = useState(reviewedSuggestionIDs);

  const {
    suggestionsByFormPath,
    suggestionsByID,
  }: Pick<
    AIOnboardingModalContextShape,
    'suggestionsByFormPath' | 'suggestionsByID'
  > = useMemo(() => {
    if (isEmpty(suggestions)) {
      return {
        suggestionsByFormPath: {
          individuals: [],
          organizations: [],
          entities: [],
          testamentaryEntities: [],
          assets: [],
          liabilities: [],
          lifeInsurancePolicies: [],
        },
        suggestionsByID: {},
      };
    }

    // Merge the whole submission scope, reference suggestions, and the
    // current page into the lookup map. Later entries win in keyBy, so the
    // current page's evidence-bearing objects take precedence over their
    // evidence-free submission-scope copies.
    const allSuggestions: AiSuggestionForReviewCard[] = [
      ...suggestionsForSubmission,
      ...(additionalReferenceSuggestions || []),
      ...suggestions,
    ];
    const suggestionsByID = keyBy(allSuggestions, (s) => s.id);

    const unacknowledgedSuggestions = suggestions.filter(
      (s) =>
        !s.acceptanceStatus && !previouslyReviewedSuggestionIDs.includes(s.id)
    );

    const grantorIds = (household.possibleGrantors ?? []).map((g) => g.id);

    return {
      suggestionsByFormPath: groupSuggestionsByFormPath(
        unacknowledgedSuggestions,
        grantorIds
      ),
      suggestionsByID,
    };
  }, [
    suggestions,
    suggestionsForSubmission,
    additionalReferenceSuggestions,
    household.possibleGrantors,
    previouslyReviewedSuggestionIDs,
  ]);

  const allSuggestions = useMemo(
    () => Object.values(suggestionsByID),
    [suggestionsByID]
  );

  const { principalNodesById, loading: principalNodesLoading } =
    useOnboardingPrincipalNodes({
      householdId: household.id,
      suggestions: allSuggestions,
    });

  return {
    isOpen,
    onClose: handleClose,
    isOperationInFlight,
    beginOperation,
    endOperation,
    loading,
    variant,
    household,
    suggestionsPagination,
    suggestionsByFormPath,
    suggestionsByID,
    suggestionsForSubmission,
    principalNodesById,
    principalNodesLoading,
    reviewedSuggestionIDs,
    onComplete,
    onSuggestionSaved,
    footerRef,
    skipNavigationOnComplete,
  };
}

export function AIOnboardingModalProvider({
  children,
  ...props
}: PropsWithChildren<AIOnboardingModalProviderProps>) {
  const value = useContextValue(props);

  return (
    <AIOnboardingModalContext.Provider value={value}>
      {children}
    </AIOnboardingModalContext.Provider>
  );
}
